import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from 'utils/user';
import {
  Connector,
  ConnectorCatalogItem,
  ConnectorRun,
  CreateConnectorBody,
  PatchConnectorBody,
} from './types';

const transformErrorResponse = (response: FetchBaseQueryError) => {
  const data = 'data' in response ? response.data : undefined;
  const error = data && typeof data === 'object' && data !== null && 'error' in data
    && typeof data.error === 'string'
    ? data.error
    : undefined;
  return {
    error,
    status: response.status,
  };
};

export const apiConnectors = createApi({
  reducerPath: 'connectorsApi',
  tagTypes: ['Connector', 'ConnectorRun'],
  baseQuery: fetchBaseQuery({
    baseUrl: `${getApiBaseUrl()}/`,
    credentials: 'include',
    prepareHeaders: (headers) => {
      headers.set('X-Client', 'web');
      return headers;
    },
  }),
  endpoints: builder => ({
    getConnectorCatalog: builder.query<ConnectorCatalogItem[], void>({
      query: () => 'connectors/catalog',
      transformResponse: (
        data: ConnectorCatalogItem[] | { catalog?: ConnectorCatalogItem[] },
      ) => (Array.isArray(data) ? data : (data?.catalog ?? [])),
      transformErrorResponse,
    }),
    getConnectors: builder.query<Connector[], void>({
      query: () => 'connectors',
      transformResponse: (data: { connectors: Connector[] }) => data.connectors,
      transformErrorResponse,
      providesTags: ['Connector'],
    }),
    addConnector: builder.mutation<Connector, CreateConnectorBody>({
      query: body => ({
        url: 'connectors',
        method: 'POST',
        body,
      }),
      transformResponse: (data: { connector: Connector }) => data.connector,
      transformErrorResponse,
      invalidatesTags: ['Connector'],
    }),
    patchConnector: builder.mutation<Connector, PatchConnectorBody>({
      query: ({ id, ...body }) => ({
        url: `connectors/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (data: { connector: Connector }) => data.connector,
      transformErrorResponse,
      invalidatesTags: ['Connector'],
    }),
    deleteConnector: builder.mutation<string, string>({
      query: id => ({
        url: `connectors/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (data: { connectorId: string }) => data.connectorId,
      transformErrorResponse,
      invalidatesTags: ['Connector'],
    }),
    getConnectorRuns: builder.query<ConnectorRun[], string>({
      query: id => `connectors/${id}/runs`,
      transformResponse: (data: { runs: ConnectorRun[] }) => data.runs,
      transformErrorResponse,
      providesTags: (_result, _error, id) => [{ type: 'ConnectorRun', id }],
    }),
    runConnector: builder.mutation<Connector, string>({
      query: id => ({
        url: `connectors/${id}/runs`,
        method: 'POST',
      }),
      transformResponse: (data: { connector: Connector }) => data.connector,
      transformErrorResponse,
      invalidatesTags: (_result, _error, id) => [
        'Connector',
        { type: 'ConnectorRun', id },
      ],
    }),
    cancelConnectorRun: builder.mutation<ConnectorRun, { connectorId: string; runId: string }>({
      query: ({ connectorId, runId }) => ({
        url: `connectors/${connectorId}/runs/${runId}/cancel`,
        method: 'POST',
      }),
      transformResponse: (data: { run: ConnectorRun }) => data.run,
      transformErrorResponse,
      invalidatesTags: (_result, _error, { connectorId }) => [
        { type: 'ConnectorRun', id: connectorId },
      ],
    }),
  }),
});

export const {
  useGetConnectorCatalogQuery,
  useGetConnectorsQuery,
  useAddConnectorMutation,
  usePatchConnectorMutation,
  useDeleteConnectorMutation,
  useGetConnectorRunsQuery,
  useRunConnectorMutation,
  useCancelConnectorRunMutation,
} = apiConnectors;
